
import React, { useCallback, useMemo, useRef, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import * as Sentry from '@sentry/react';
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AnalyticsProtectedRoute } from "@/components/admin/AnalyticsProtectedRoute";
import { OrgRestrictedRoute } from "@/components/admin/OrgRestrictedRoute";
import { RequireOrgMembership } from "@/components/admin/RequireOrgMembership";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { EntradaPorPermissao } from "@/components/auth/EntradaPorPermissao";
import { SuperAdminProtectedRoute } from "@/components/admin/SuperAdminProtectedRoute";
import { useErrorHandler } from "@/hooks/app/useErrorHandler";
import { OrganizationProvider, useOrganization } from "@/contexts/OrganizationContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { LazyPage } from "@/components/LazyPage";
import { PageProvider } from "@/contexts/PageContext";
import { ActivityProvider } from "@/contexts/ActivityContext";
import { GlobalActivityScheduleModal } from "@/components/activities/GlobalActivityScheduleModal";
import { AppRenderController } from "@/components/ui/AppRenderController";
import { useAppInitialization } from "@/hooks/app/useAppInitialization";
import { useRealtimeLifecycle } from "@/hooks/app/useRealtimeLifecycle";
import { useCrmRealtime } from "@/hooks/app/useCrmRealtime";
import { AppLayout } from "@/components/layout/AppLayout";
import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { TourProvider } from "@/features/tour/TourContext";
import "@/utils/clearAppCache"; // Disponibiliza clearAppCache globalmente
import { logDiag } from "@/utils/appDiagnostics";
import { isPortalContext } from "@/lib/portalRoute";
import { rememberPostLoginTarget } from "@/lib/postLoginRedirect";
// Lazy load all pages com retry para evitar erros de carregamento
const SiteAnalysisView = lazyWithRetry(() => import("./pages/public/SiteAnalysisView"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/public/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/public/TermsOfService"));
// Booking Links (Agendamento) — páginas públicas (sem auth) + admin autenticado
const BookingPublicPage = lazyWithRetry(() => import("./pages/public/BookingPublicPage"));
const ProposalLivePage = lazyWithRetry(() => import("./features/proposals/ProposalLivePage"));
const ProposalPublicPage = lazyWithRetry(() => import("./features/proposals/ProposalPublicPage"));
const BookingManagePage = lazyWithRetry(() => import("./pages/public/BookingManagePage"));
const AgendamentoList = lazyWithRetry(() => import("./pages/agendamento/AgendamentoList"));
const AgendamentoEditor = lazyWithRetry(() => import("./pages/agendamento/AgendamentoEditor"));
const AgendamentoDisponibilidade = lazyWithRetry(() => import("./pages/agendamento/AgendamentoDisponibilidade"));
const Index = lazyWithRetry(() => import("./pages/Index"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const DashboardV2 = lazyWithRetry(() => import("./pages/DashboardV2"));
const VendedorPortal = lazyWithRetry(() => import("./pages/vendedor"));
const OperacoesPortal = lazyWithRetry(() => import("./pages/operacoes"));
const MateriaisPortal = lazyWithRetry(() => import("./pages/materiais"));
const MarketingPage = lazyWithRetry(() => import("./pages/Marketing"));
const ObjetivosPortal = lazyWithRetry(() => import("./pages/objetivos"));
const FinanceiroPortal = lazyWithRetry(() => import("./pages/financeiro"));
const RedirectComissionamento = lazyWithRetry(() => import("./pages/financeiro/RedirectComissionamento"));
const ManagerPage = lazyWithRetry(() => import("./pages/ManagerPage"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const Sequences = lazyWithRetry(() => import("./pages/Sequences"));
const SequenceCreate = lazyWithRetry(() => import("./pages/SequenceCreate"));
const SequenceEdit = lazyWithRetry(() => import("./pages/SequenceEdit"));
const SequenceDetails = lazyWithRetry(() => import("./pages/SequenceDetails"));
const SequenceReview = lazyWithRetry(() => import("./pages/SequenceReview"));
const Activities = lazyWithRetry(() => import("./pages/Activities"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const FocusExecutionMode = lazyWithRetry(() => import("./pages/FocusExecutionMode"));
function RedirectProdutos() {
  const { search } = useLocation();
  return <Navigate to={`/produtos${search}`} replace />;
}
const Metas = lazyWithRetry(() => import("./pages/Metas"));
// Produtos: em Vendas desde 09/10/2026 (antes /operacoes/produtos).
const ProdutosPage = lazyWithRetry(() => import("./pages/produtos"));
const RelatorioMidhausShell = lazyWithRetry(() => import("./pages/relatorio-midhaus"));
const RelatorioMidhausVisaoGeral = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/VisaoGeral"));
const RelatorioMidhausLigacoes = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/ligacoes"));
const RelatorioMidhausReunioes = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/reunioes"));
const RelatorioMidhausDeals = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/deals"));
const RelatorioMidhausPaginas = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/paginas"));
const RelatorioMidhausAtividades = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/atividades"));
const RelatorioMidhausPreVendas = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/supervisao-pre-vendas"));
const RelatorioMidhausClosers = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/closers"));
const RelatorioMidhausMarketingVendas = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/marketing-vendas"));
const RelatorioMidhausResultados = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/resultados"));
const GestaoAgendaPage = lazyWithRetry(() => import("./pages/GestaoAgenda"));
const RelatorioMidhausLigacoesOrfas = lazyWithRetry(() => import("./pages/relatorio-midhaus/views/ligacoes-orfas"));

// Relatório Atticus — cópia do da Midhaus (ver src/pages/relatorio-atticus/orgConfig.ts).
// O Painel de Resultados ainda não entra: o motor está acoplado ao cadastro da Midhaus.
const RelatorioAtticusShell = lazyWithRetry(() => import("./pages/relatorio-atticus"));
// Pastas do jurídico com os dados do cliente mascarados no banco (docs/specs/pastas-demonstracao.md).
const PastasDemonstracaoPage = lazyWithRetry(() => import("./pages/pastas-demonstracao"));
// Pastas do escritório cliente do rateio Atticus (preset `atticus_escritorio`); a máscara é do banco.
const PastasEscritorioPage = lazyWithRetry(() => import("./pages/pastas-escritorio"));
const RelatorioAtticusVisaoGeral = lazyWithRetry(() => import("./pages/relatorio-atticus/views/VisaoGeral"));
const RelatorioAtticusLigacoes = lazyWithRetry(() => import("./pages/relatorio-atticus/views/ligacoes"));
const RelatorioAtticusReunioes = lazyWithRetry(() => import("./pages/relatorio-atticus/views/reunioes"));
const RelatorioAtticusDeals = lazyWithRetry(() => import("./pages/relatorio-atticus/views/deals"));
const RelatorioAtticusPaginas = lazyWithRetry(() => import("./pages/relatorio-atticus/views/paginas"));
const RelatorioAtticusAtividades = lazyWithRetry(() => import("./pages/relatorio-atticus/views/atividades"));
const RelatorioAtticusPreVendas = lazyWithRetry(() => import("./pages/relatorio-atticus/views/supervisao-pre-vendas"));
const RelatorioAtticusClosers = lazyWithRetry(() => import("./pages/relatorio-atticus/views/closers"));
const RelatorioAtticusMarketingVendas = lazyWithRetry(() => import("./pages/relatorio-atticus/views/marketing-vendas"));
const RelatorioAtticusLigacoesOrfas = lazyWithRetry(() => import("./pages/relatorio-atticus/views/ligacoes-orfas"));
// Relatórios › Operações (Carteira e Teses). Só a Atticus por ora; ver src/pages/relatorio-operacoes.
const RelatorioOperacoesShell = lazyWithRetry(() => import("./pages/relatorio-operacoes"));
const RelatorioOperacoesIndex = lazyWithRetry(() => import("./pages/relatorio-operacoes/RelatorioOperacoesIndex"));
const RelatorioOperacoesCarteira = lazyWithRetry(() => import("./pages/relatorio-operacoes/views/Carteira"));
const RelatorioOperacoesTeses = lazyWithRetry(() => import("./pages/relatorio-operacoes/views/Teses"));
const RelatorioSquad = lazyWithRetry(() => import("./pages/relatorio-squad"));
import { SQUAD_ORG_IDS, isSquadOrg } from "./pages/relatorio-squad/squadConfig";
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const ZapSignSettings = lazyWithRetry(() => import("./pages/contratos/ZapSignSettings"));
const ContractsDashboard = lazyWithRetry(() => import("./pages/contratos/ContractsDashboard"));
const AdminOrganizations = lazyWithRetry(() => import("./pages/AdminOrganizations"));
const Profile = lazyWithRetry(() => import("./pages/Profile"));
const SuperAdmin = lazyWithRetry(() => import("./pages/SuperAdmin"));
const Supervisao = lazyWithRetry(() => import("./pages/supervisao"));
const RelatoriosPortal = lazyWithRetry(() => import("./pages/relatorios"));
const PortalSolicitarAcesso = lazyWithRetry(() => import("./pages/relatorios/access/SolicitarAcesso"));
const PortalDecisaoAcesso = lazyWithRetry(() => import("./pages/relatorios/access/DecisaoAcesso"));
const SemAcesso = lazyWithRetry(() => import("./pages/SemAcesso"));
const LossReasons = lazyWithRetry(() => import("./pages/LossReasons"));

const Deals = lazyWithRetry(() => import("./pages/Deals"));
const DealCreate = lazyWithRetry(() => import("./pages/DealCreate"));
const DealProfile = lazyWithRetry(() => import("./pages/DealProfileV2"));
const DealDiagnosis = lazyWithRetry(() => import("./pages/DealDiagnosis"));
const DealEdit = lazyWithRetry(() => import("./pages/DealEdit"));
const DealsKanban = lazyWithRetry(() => import("./pages/DealsKanban"));
const DealsForecast = lazyWithRetry(() => import("./pages/DealsForecast"));
const DealImport = lazyWithRetry(() => import("./pages/DealImport"));
const CallTranscription = lazyWithRetry(() => import("./pages/CallTranscription"));
const MeetingTranscription = lazyWithRetry(() => import("./pages/MeetingTranscription"));

const Companies = lazyWithRetry(() => import("./pages/Companies"));
const CompanyCreate = lazyWithRetry(() => import("./pages/CompanyCreate"));
const CompanyProfile = lazyWithRetry(() => import("./pages/CompanyProfile"));
const CompanyEdit = lazyWithRetry(() => import("./pages/CompanyEdit"));
const Leads = lazyWithRetry(() => import("./pages/Leads"));
const LeadCreate = lazyWithRetry(() => import("./pages/LeadCreate"));
const LeadProfile = lazyWithRetry(() => import("./pages/LeadProfile"));
const LeadEdit = lazyWithRetry(() => import("./pages/LeadEdit"));
const LeadImport = lazyWithRetry(() => import("./pages/LeadImport"));
const Segments = lazyWithRetry(() => import("./pages/Segments"));
const SegmentDetails = lazyWithRetry(() => import("./pages/SegmentDetails"));
const Automations = lazyWithRetry(() => import("./pages/Automations"));
const FollowUps = lazyWithRetry(() => import("./pages/FollowUps"));
const AutomationRuleCreate = lazyWithRetry(() => import("./pages/AutomationRuleCreate"));
const AutomationRuleEdit = lazyWithRetry(() => import("./pages/AutomationRuleEdit"));
const AutomationQueueEdit = lazyWithRetry(() => import("./pages/AutomationQueueEdit"));
const Chat = lazyWithRetry(() => import("./pages/Chat"));
const EmailInbox = lazyWithRetry(() => import("./pages/EmailInbox").then(module => ({ default: module.EmailInbox })));
// Fim do popup "Conectar Outlook" pelo EmailEngine (mail-oauth-callback redireciona para cá).
const EmailOAuthDone = lazyWithRetry(() => import("./pages/EmailOAuthDone"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const InviteAccept = lazyWithRetry(() => import("./pages/InviteAccept"));
const AgentsList = lazyWithRetry(() => import("./components/agents/AgentsList").then(module => ({ default: module.default })));
const AgentConfigPage = lazyWithRetry(() => import("./pages/AgentConfigPage"));
const StyleGuide = lazyWithRetry(() => import("./pages/style-guide/index"));
const Docs = lazyWithRetry(() => import("./pages/Docs"));
const LeadScoringRedirect = lazyWithRetry(() => import("./pages/LeadScoringRedirect"));

// Análises customizáveis (shell estilo Insights + painel)
const MetricasInsightsLayout = lazyWithRetry(() => import("./features/analysis/layout/MetricasInsightsLayout"));
const MetricasIndexPage = lazyWithRetry(() => import("./features/analysis/pages/MetricasIndexPage"));
const AnalysisLabDashboard = lazyWithRetry(() => import("./features/analysis/pages/DashboardPage"));

// WhatsApp API Oficial (Cloud API / Meta) — disparo de templates
const WhatsAppOficial = lazyWithRetry(() => import("./features/whatsapp-oficial/pages/WhatsAppOficialPage"));
// Dashboard de Saúde dos Números (Cloud API / Meta) — monitoramento preventivo
const WhatsAppSaude = lazyWithRetry(() => import("./features/whatsapp-health/pages/WhatsAppHealthPage"));

// SMS — disparo em massa, templates e conexão do provedor
const SmsPage = lazyWithRetry(() => import("./features/sms/pages/SmsPage"));

// Vox AI
const VoxAssistantList = lazyWithRetry(() => import("./pages/vox/VoxAssistantList"));
const VoxAssistantCreate = lazyWithRetry(() => import("./pages/vox/VoxAssistantCreate"));
const VoxAssistantDetail = lazyWithRetry(() => import("./pages/vox/VoxAssistantDetail"));

const VoxCampaignList = lazyWithRetry(() => import("./pages/vox/VoxCampaignList"));
const VoxCampaignCreate = lazyWithRetry(() => import("./pages/vox/VoxCampaignCreate"));
const VoxCampaignDetail = lazyWithRetry(() => import("./pages/vox/VoxCampaignDetail"));
const VoxReports = lazyWithRetry(() => import("./pages/vox/VoxReports"));

// Discadora Sonax
const SonaxCampaignList = lazyWithRetry(() => import("./pages/sonax/SonaxCampaignList"));
const SonaxCampaignCreate = lazyWithRetry(() => import("./pages/sonax/SonaxCampaignCreate"));
const SonaxCampaignDetail = lazyWithRetry(() => import("./pages/sonax/SonaxCampaignDetail"));
const SonaxCampaignEdit = lazyWithRetry(() => import("./pages/sonax/SonaxCampaignEdit"));
const SonaxDialerOperate = lazyWithRetry(() => import("./pages/sonax/SonaxDialerOperate"));
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { ComposeEmailModal } from "@/components/email/ComposeEmailModal";
import { useNylasSendEmail } from "@/hooks/email";
import { EmailComposeData } from "@/types/email";
import { AuthLoader } from "@/components/ui/AuthLoader";
import { useSonaxWithAppState } from "@/hooks/integrations/useSonaxWithAppState";
import { SonaxWidgetProvider } from "@/contexts/SonaxWidgetContext";
import { ChatVisibilityProvider } from "@/contexts/ChatVisibilityContext";
import { ConfirmDialogProvider } from "@/contexts/ConfirmDialogProvider";
import { useMountDebug } from "@/hooks/shared/useMountDebug";
import { EmailContext } from "@/contexts/EmailContext";
import { MARKETING_DEMO } from "@/features/marketing/demoFlag";

interface PrivateRouteProps {
  element: JSX.Element;
  authLoading: boolean;
  session: any;
}

// PrivateRoute — NUNCA desmonta children após primeira sessão.
// Usa CSS visibility/overlay para TODOS os estados de loading (auth + org).
// Isso previne destruição de hooks/estado do EmailInbox e outros componentes pesados.
const PrivateRoute: React.FC<PrivateRouteProps> = ({
  element,
  authLoading,
  session,
}) => {
  const { loading: orgLoading } = useOrganization();
  const hasEverHadSessionRef = useRef(false);

  if (session) {
    hasEverHadSessionRef.current = true;
  }

  // Antes da primeira sessão: bloqueio hard (nada para renderizar)
  if (!hasEverHadSessionRef.current) {
    if (authLoading) {
      return <AuthLoader message="Verificando autenticação..." />;
    }
    if (!session) {
      return <Navigate to="/auth" replace />;
    }
  }

  // Após primeira sessão: SEMPRE renderizar children, usar CSS para loading states
  const isLoading = authLoading || orgLoading;
  if (isLoading) {
    logDiag('PRIVATE_ROUTE', `loading overlay: authLoading=${authLoading} orgLoading=${orgLoading}`);
  }

  return (
    <>
      {isLoading && (
        <div className="absolute inset-0 z-50 bg-background">
          <AuthLoader message={authLoading ? "Verificando autenticação..." : "Carregando organização..."} />
        </div>
      )}
      <div style={{ visibility: isLoading ? 'hidden' : 'visible', height: '100%' }}>
        <LazyPage>{element}</LazyPage>
      </div>
    </>
  );
};

// Layout-route do CRM: provê o shell (Navbar + Sidebar) via <Outlet />. O console Super-Admin
// fica FORA deste layout (rota irmã em /super-admin/*), por isso é uma página independente — sem
// topbar e sem depender do fluxo de organização. O Suspense interno mantém o shell montado
// enquanto as páginas lazy carregam (evita flicker da sidebar a cada navegação).
const AppLayoutOutlet: React.FC = () => (
  <AppLayout>
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  </AppLayout>
);


// Orgs do "Squad" usam o Relatório Squad ("Dashboard") como página inicial; o
// dashboard padrão fica oculto para elas (nav escondido no Sidebar). Nas demais
// orgs, renderiza o destino normal.
const SquadHomeRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentOrg, loading } = useOrganization();
  if (loading) return <AuthLoader message="Carregando organização..." />;
  if (isSquadOrg(currentOrg?.id)) return <Navigate to="/relatorio-squad" replace />;
  return <>{children}</>;
};

// Portal externo, sem sessão: guarda a intenção e manda para o login.
// Sem isto a rota cairia no `*` da árvore não autenticada e renderizaria NotFound — é o bug
// que /supervisao/* tem hoje: o que falta na segunda árvore vira 404, não redirect para /auth.
const PortalAuthRedirect: React.FC = () => {
  const location = useLocation();
  const target = `${location.pathname}${location.search}`;
  useEffect(() => {
    rememberPostLoginTarget(target);
  }, [target]);
  return <Navigate to={`/auth?redirect=${encodeURIComponent(target)}`} replace />;
};

const LocationDebug: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    console.info("[LocationDebug] location change", {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      key: location.key
    });
  }, [location.pathname, location.search, location.hash, location.key]);
  return null;
};

/** Must be rendered inside <BrowserRouter> — controls chat channel lifecycle by route + visibility */
const RealtimeLifecycleManager: React.FC = () => {
  useRealtimeLifecycle();
  useCrmRealtime();
  return null;
};

const AppContent = () => {
  useMountDebug("AppContent");
  useErrorHandler();
  const { isInitializing, isComplete, progress, currentStep } = useAppInitialization();

  // Detect password recovery flow from Supabase redirect and redirect to /auth page
  // Supabase redirects to the root with tokens in the hash: https://crm.midhaus.com.br#access_token=...&type=recovery
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    
    // `invite` entra junto com `recovery`: o link de convite do Supabase cria a sessão mas
    // NÃO define senha. Sem mandar para /auth, o leitor externo entraria no portal nesta
    // visita e ficaria sem conseguir voltar depois — teria que pedir "esqueci minha senha"
    // toda vez. Em /auth o modo update-password deixa ele escolher a senha primeiro.
    if ((type === 'recovery' || type === 'invite') && accessToken && refreshToken) {
      // Já em /auth: a página trata. Redirecionar de novo recarregava sem o `?mode=update-password`
      // e, com o hash já consumido pelo cliente do Supabase, o convite caía direto na home sem
      // pedir a senha.
      if (window.location.pathname === '/auth') return;
      console.log('🔐 Password recovery flow detected, redirecting to /auth');
      // Leva o modo e o tipo na query: o hash some assim que o cliente do Supabase o lê.
      window.location.href = `/auth?mode=update-password&type=${type}${window.location.hash}`;
      return;
    }
  }, []);

  // Sonax widget temporarily disabled
  const [session, setSession] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showFloatingCompose, setShowFloatingCompose] = useState(false);
  const [composeInitialData, setComposeInitialData] = useState<{ to: string[]; cc: string[]; bcc: string[]; subject: string; body: string } | undefined>(undefined);
  const [appFullyLoaded, setAppFullyLoaded] = useState(false);
  const { sendEmail } = useNylasSendEmail();

  // Estado para controlar quando o Sonax pode ser carregado
  const [canLoadSonax, setCanLoadSonax] = useState(false);

  // Hook do Sonax controlado pelo estado da aplicação
  const sonaxWidget = useSonaxWithAppState({
    canLoadSonax,
    appFullyLoaded,
    session
  });

  // Sonax: apenas esconde o #icon nativo (botão flutuante azul).
  // Todo o controle de visibilidade/posicionamento é feito pelo SonaxHeaderButton.
  useEffect(() => {
    if (!appFullyLoaded || !canLoadSonax) return;

    const hideNativeIcon = () => {
      const icon = document.getElementById('icon');
      if (icon) icon.style.setProperty('display', 'none', 'important');
      const closeWidget = document.getElementById('close-widget');
      if (closeWidget) closeWidget.style.setProperty('display', 'none', 'important');
    };

    hideNativeIcon();
    const interval = setInterval(hideNativeIcon, 2000);
    return () => clearInterval(interval);
  }, [appFullyLoaded, canLoadSonax]);


  useEffect(() => {
    let mounted = true;
    let bootstrapPromise: Promise<void> | null = null;
    // Timeout de segurança - se getSession não responder em 5 segundos, limpar e continuar
    const sessionTimeout = setTimeout(() => {
      if (mounted) {
        console.warn('Timeout ao obter sessão - limpando cache de autenticação');
        // Tentar limpar sessão corrompida
        try {
          localStorage.removeItem('supabase.auth.token');
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
              localStorage.removeItem(key);
            }
          });
        } catch (e) {
          console.error('Erro ao limpar cache de autenticação:', e);
        }
        setSession(null);
        setAuthLoading(false);
      }
    }, 5000);

    // Função para bootstrap que evita múltiplas chamadas
    const bootstrapUserOrg = async () => {
      // Leitor externo do portal de relatórios NUNCA pode passar por aqui: bootstrap_user_org
      // cria organização + membership 'admin' para quem não tem membership, e membership ativa
      // abre a RLS de deals/leads/activities/ligacoes — o oposto do modelo do portal.
      //
      // O guard fica DENTRO da função (e não nos dois call sites) para não depender de ninguém
      // lembrar dele num refactor. Defesa em profundidade: o gate real é o do banco, que
      // retorna NULL para quem tem linha ativa em bu_viewers.
      if (isPortalContext()) {
        return;
      }
      if (bootstrapPromise) {
        return bootstrapPromise;
      }
      bootstrapPromise = (async () => {
        try {
          await supabase.rpc("bootstrap_user_org");
        } catch (error) {
          console.error('Error bootstrapping user org:', error);
        } finally {
          bootstrapPromise = null;
        }
      })();
      return bootstrapPromise;
    };

    // Verificar sessão IMEDIATAMENTE (síncrono do localStorage)
    // getSession() é rápido porque lê do localStorage primeiro
    supabase.auth.getSession()
      .then(({ data, error }) => {
        if (sessionTimeout) clearTimeout(sessionTimeout);
        
        if (mounted) {
          if (error) {
            console.error('Error getting session:', error);
            // Se houver erro, limpar cache corrompido
            try {
              localStorage.removeItem('supabase.auth.token');
              Object.keys(localStorage).forEach((key) => {
                if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
                  localStorage.removeItem(key);
                }
              });
            } catch (e) {
              // Ignorar erros de limpeza
            }
            setSession(null);
          } else {
            // Validar se a sessão é válida
            if (data.session?.user) {
              setSession(data.session);
              bootstrapUserOrg();
            } else {
              setSession(null);
            }
          }
          setAuthLoading(false);
        }
      })
      .catch((error) => {
        if (sessionTimeout) clearTimeout(sessionTimeout);
        console.error('Error in getSession:', error);
        if (mounted) {
          // Limpar cache em caso de erro
          try {
            localStorage.removeItem('supabase.auth.token');
            Object.keys(localStorage).forEach((key) => {
              if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
                localStorage.removeItem(key);
              }
            });
          } catch (e) {
            // Ignorar erros de limpeza
          }
          setSession(null);
          setAuthLoading(false);
        }
      });

    // Escutar mudanças de autenticação (para login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      logDiag('AUTH', `onAuthStateChange: ${event} | userId=${s?.user?.id?.slice(0,8) ?? 'null'}`);
      if (mounted) {
        // Handle PASSWORD_RECOVERY event - redirect to auth page for password reset
        if (event === 'PASSWORD_RECOVERY') {
          console.log('🔐 PASSWORD_RECOVERY event detected, redirecting to /auth for password reset');
          // Set session but redirect to auth page for password update
          setSession(s);
          setAuthLoading(false);
          // Redirect to auth page with update-password mode. Já em /auth, não recarrega: perderia
          // a query (`primeiro_acesso`) que a página leu no mount.
          if (window.location.pathname !== '/auth') {
            window.location.href = '/auth?mode=update-password';
          }
          return;
        }
        
        // Tratar eventos que devem atualizar a sessão
        if (event === 'SIGNED_OUT') {
          setSession(null);
          setAuthLoading(false);
        } else if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          // Check if this is actually a recovery flow (tokens in URL with type=recovery)
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const type = hashParams.get('type');
          // Convite também: o Supabase emite SIGNED_IN (não PASSWORD_RECOVERY) e a pessoa ainda
          // não tem senha.
          if ((type === 'recovery' || type === 'invite') && event === 'SIGNED_IN') {
            setSession(s);
            setAuthLoading(false);
            if (window.location.pathname !== '/auth') {
              window.location.href = `/auth?mode=update-password&type=${type}`;
            }
            return;
          }

          // SIGNED_IN dispara periodicamente (~60s) no token refresh do Supabase.
          // Usar functional update para manter referência anterior quando user é o mesmo,
          // evitando re-render em cascata (AppContent → PrivateRoute → EmailInbox).
          let hadPrevSession = false;
          setSession(prev => {
            hadPrevSession = !!prev;
            if (!prev) { logDiag('SESSION', 'setSession: no prev → accepting new'); return s; }
            if (prev.user?.id === s?.user?.id) {
              logDiag('SESSION', 'setSession: same user → keeping prev ref');
              return prev;
            }
            logDiag('SESSION', `setSession: user changed ${prev.user?.id?.slice(0,8)} → ${s?.user?.id?.slice(0,8)}`);
            return s;
          });
          setAuthLoading(false);

          // Bootstrap apenas no primeiro login real (INITIAL_SESSION ou SIGNED_IN sem sessão prévia)
          if (s?.user && (event === 'INITIAL_SESSION' || !hadPrevSession)) {
            bootstrapUserOrg();
          }
        } else if (event === 'TOKEN_REFRESHED') {
          // TOKEN_REFRESHED: atualizar sessão silenciosamente sem causar re-render em cascata
          setSession(prev => {
            if (!prev) return s;
            if (prev.user?.id === s?.user?.id) {
              return prev;
            }
            return s;
          });
        }
      }
    });

    return () => {
      mounted = false;
      if (sessionTimeout) clearTimeout(sessionTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const handleSendEmail = async (data: EmailComposeData) => {
    const result = await sendEmail(data);
    if (result.success) {
      setShowFloatingCompose(false);
    }
    return result;
  };

  const handleAppFullyLoaded = useCallback(() => {
    // Sincronizar estado quando AppRenderController notificar que está pronto
    setAppFullyLoaded(true);
    setCanLoadSonax(true);
  }, []);

  const handleInitializationComplete = useCallback(() => {
    // Aplicação inicializada
  }, []);

  const emailContextValue = useMemo(() => ({
    openCompose: (options?: { to?: string }) => {
      if (options?.to) {
        setComposeInitialData({ to: [options.to], cc: [], bcc: [], subject: '', body: '' });
      }
      setShowFloatingCompose(true);
    },
    closeCompose: () => {
      setComposeInitialData(undefined);
      setShowFloatingCompose(false);
    }
  }), []);
  const chatElement = useMemo(() => <Chat />, []);

  // LATCH: Uma vez que AppLayout foi mostrado, NUNCA desmontar.
  // Se session/authLoading fliparem transientemente, a árvore inteira (AppLayout → Routes → EmailInbox)
  // seria destruída. O latch previne isso. Sign-out real usa Navigate ou window.location.
  const shouldRenderAppLayoutRef = useRef(false);
  if (session && !authLoading) {
    shouldRenderAppLayoutRef.current = true;
  }
  const shouldRenderAppLayout = shouldRenderAppLayoutRef.current;

  return (
    <AppRenderController
      isInitializing={isInitializing}
      isComplete={isComplete}
      progress={progress}
      currentStep={currentStep}
      session={session}
      authLoading={authLoading}
      onInitializationComplete={handleInitializationComplete}
      onAppFullyLoaded={handleAppFullyLoaded}
    >
      <EmailContext.Provider value={emailContextValue}>
        <ChatVisibilityProvider>
          <BrowserRouter>
            <LocationDebug />
            <RealtimeLifecycleManager />
            {shouldRenderAppLayout ? (
              <TourProvider>
                <Suspense fallback={null}>
                <Routes>
                {/* Console Super-Admin — layout próprio, FORA do AppLayout e do fluxo de org. */}
                <Route path="/super-admin/*" element={<LazyPage><SuperAdmin /></LazyPage>} />
                {/* Área de Supervisão (cross-org) — layout próprio, FORA do AppLayout e do fluxo de org. */}
                <Route path="/supervisao/*" element={<LazyPage><Supervisao /></LazyPage>} />
                {/* Portal externo — telas PÚBLICAS do pedido de acesso. Declaradas também na
                    árvore não autenticada (quem pede acesso não tem login). O React Router 6
                    casa por RANKING, não por ordem: o segmento estático já ganha do splat
                    abaixo e do `*` interno do RelatoriosPortal. Estarem antes é legibilidade —
                    não "conserte" a ordem achando que era ela que fazia funcionar. */}
                <Route path="/relatorios/acesso/decisao" element={<LazyPage><PortalDecisaoAcesso /></LazyPage>} />
                <Route path="/relatorios/:buSlug/solicitar-acesso" element={<LazyPage><PortalSolicitarAcesso /></LazyPage>} />
                {/* Portal externo de relatórios — leitor SEM membership. Irmã do AppLayout de
                    propósito: zero chrome do CRM (sem sidebar, sem busca global, sem link que
                    leve para dentro do produto). */}
                <Route path="/relatorios/:buSlug/*" element={<LazyPage><RelatoriosPortal /></LazyPage>} />
                {/* Autenticado mas sem membership E sem bu_viewers: nunca o CRM vazio. */}
                <Route path="/sem-acesso" element={<LazyPage><SemAcesso /></LazyPage>} />
                {/* Popup do OAuth do Outlook (EmailEngine): só avisa a janela que abriu e fecha. Fora do layout. */}
                <Route path="/email/conectado" element={<LazyPage><EmailOAuthDone /></LazyPage>} />
                {/* Login/criar senha FORA do layout: o link de convite já abre a sessão, e dentro do
                    shell a tela "Criar seu acesso" aparecia com menu e cabeçalho, como página interna. */}
                <Route path="/auth" element={<LazyPage><Auth /></LazyPage>} />
                {/* Demais rotas — shell padrão do CRM (Navbar + Sidebar) via layout route. */}
                <Route element={<AppLayoutOutlet />}>
                <Route path="/" element={<SquadHomeRedirect><LazyPage><Index /></LazyPage></SquadHomeRedirect>} />
                {/*
                  `/dashboard` agora é o Dashboard v2. O `SquadHomeRedirect` continua na frente:
                  as orgs do Squad seguem indo para o Relatório Squad, que é a home delas.

                  O dashboard anterior está vivo em `/dashboard-legado`, SEM item de menu — as
                  abas "Funil & Operação" e "Cadências" ainda não têm equivalente no v2 e servem
                  12 e 4 organizações. Quando o uso da rota legada zerar, ela sai junto com
                  `pages/Dashboard.tsx`.
                */}
                <Route path="/dashboard" element={<PrivateRoute authLoading={authLoading} session={session} element={<SquadHomeRedirect><EntradaPorPermissao k="inicio.dashboard.view"><DashboardV2 /></EntradaPorPermissao></SquadHomeRedirect>} />} />
                <Route path="/dashboard-legado" element={<PrivateRoute authLoading={authLoading} session={session} element={<SquadHomeRedirect><Dashboard /></SquadHomeRedirect>} />} />
                {/* quem tiver `/dashboard-v2` salvo ou colado num chat continua chegando */}
                <Route path="/dashboard-v2" element={<Navigate to="/dashboard" replace />} />
                {/* O Painel de Resultados virou view do Relatório Midhaus (/relatorio-midhaus/resultados),
                    onde o OrgRestrictedRoute dá bloqueio de acesso real. Redirect mantém links antigos. */}
                <Route path="/resultados" element={<Navigate to="/relatorio-midhaus/resultados" replace />} />
                {/* Portal: as views são rotas internas (/vendedor/metas, /desempenho, /leads) */}
                <Route path="/vendedor/*" element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="inicio.vendedor.view"><VendedorPortal /></RequirePermission>} />} />
                {/* Portal Financeiro: comissões pagas (regras · apuração) e recebidas. */}
                <Route path="/financeiro/*" element={<PrivateRoute authLoading={authLoading} session={session} element={<FinanceiroPortal />} />} />
                {/* Legado: `/comissionamento/*` virou `/financeiro/comissoes-pagas/*`. O redirect
                    preserva o resto do caminho — `/apuracao/:userId` e um deep link vivo. */}
                <Route path="/comissionamento/*" element={<PrivateRoute authLoading={authLoading} session={session} element={<RedirectComissionamento />} />} />
                {/* Estas rotas eram abertas por URL para qualquer autenticado: a Sidebar escondia o
                    item e o endereço abria. Agora leem a MESMA chave que `isRouteVisible` usa, então
                    o guard NÃO tira nada de quem já enxerga o menu — só fecha o deep link. Medido em
                    13/09: ~1.763 de 1.920 memberships ativas têm estas chaves. */}
                <Route path="/gestor" element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="desempenho.gestor.view"><ManagerPage /></RequirePermission>} />} />
                <Route path="/chat" element={<PrivateRoute authLoading={authLoading} session={session} element={chatElement} />} />
                <Route path="/chat/:conversationId" element={<PrivateRoute authLoading={authLoading} session={session} element={chatElement} />} />
            <Route path="/email" element={<PrivateRoute authLoading={authLoading} session={session} element={<EmailInbox />} />} />
            <Route path="/analytics" element={<PrivateRoute authLoading={authLoading} session={session} element={<AnalyticsProtectedRoute><Analytics /></AnalyticsProtectedRoute>} />} />
            <Route path="/leads" element={<PrivateRoute authLoading={authLoading} session={session} element={<Leads />} />} />
            <Route path="/leads/import" element={<PrivateRoute authLoading={authLoading} session={session} element={<LeadImport />} />} />
            <Route path="/leads/new" element={<PrivateRoute authLoading={authLoading} session={session} element={<LeadCreate />} />} />
            <Route path="/leads/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<LeadProfile />} />} />
            <Route path="/leads/:id/edit" element={<PrivateRoute authLoading={authLoading} session={session} element={<LeadEdit />} />} />
            <Route path="/segments" element={<PrivateRoute authLoading={authLoading} session={session} element={<Segments />} />} />
            <Route path="/segments/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<SegmentDetails />} />} />
            <Route path="/automations" element={<PrivateRoute authLoading={authLoading} session={session} element={<Automations />} />} />
            <Route path="/automations/new" element={<PrivateRoute authLoading={authLoading} session={session} element={<AutomationRuleCreate />} />} />
            <Route path="/automations/queues/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<AutomationQueueEdit />} />} />
            <Route path="/automations/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<AutomationRuleEdit />} />} />
            <Route path="/follow-ups" element={<PrivateRoute authLoading={authLoading} session={session} element={<FollowUps />} />} />
            <Route path="/companies" element={<PrivateRoute authLoading={authLoading} session={session} element={<Companies />} />} />
            <Route path="/companies/new" element={<PrivateRoute authLoading={authLoading} session={session} element={<CompanyCreate />} />} />
            <Route path="/companies/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<CompanyProfile />} />} />
            <Route path="/companies/:id/edit" element={<PrivateRoute authLoading={authLoading} session={session} element={<CompanyEdit />} />} />
            <Route path="/sequences" element={<PrivateRoute authLoading={authLoading} session={session} element={<Sequences />} />} />
            <Route path="/sequences/create" element={<PrivateRoute authLoading={authLoading} session={session} element={<SequenceCreate />} />} />
            <Route path="/sequences/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<SequenceDetails />} />} />
            <Route path="/sequences/:id/edit" element={<PrivateRoute authLoading={authLoading} session={session} element={<SequenceEdit />} />} />
            <Route path="/sequences/:id/review" element={<PrivateRoute authLoading={authLoading} session={session} element={<SequenceReview />} />} />
            <Route path="/activities" element={<PrivateRoute authLoading={authLoading} session={session} element={<Activities />} />} />
            <Route path="/activities/focus" element={<PrivateRoute authLoading={authLoading} session={session} element={<FocusExecutionMode />} />} />
            <Route path="/activities/new" element={<Navigate to="/activities" replace />} />
            <Route path="/notifications" element={<PrivateRoute authLoading={authLoading} session={session} element={<Notifications />} />} />
            {/* Produtos mora em Vendas desde 09/10/2026 (`clientes.produtos.view`). Os endereços antigos
                (/products e /operacoes/produtos, com o deep-link ?editProduct=) chegam aqui. */}
            <Route path="/produtos" element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="clientes.produtos.view"><ProdutosPage /></RequirePermission>} />} />
            <Route path="/products" element={<PrivateRoute authLoading={authLoading} session={session} element={<RedirectProdutos />} />} />
            <Route path="/marketing" element={<PrivateRoute authLoading={authLoading} session={session} element={MARKETING_DEMO ? <MarketingPage /> : <RequirePermission k="desempenho.marketing.view"><MarketingPage /></RequirePermission>} />} />
            <Route path="/metas" element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="desempenho.metas.view"><Metas /></RequirePermission>} />} />
            <Route
              path="/relatorio-midhaus/ligacoes-orfas"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<RequireOrgMembership orgId="9d298125-4eb6-4c94-9168-b619bfc16b6a"><RelatorioMidhausLigacoesOrfas /></RequireOrgMembership>} />}
            />
            <Route
              path="/relatorio-midhaus"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<OrgRestrictedRoute allowedOrgIds={['9d298125-4eb6-4c94-9168-b619bfc16b6a']}><RelatorioMidhausShell /></OrgRestrictedRoute>} />}
            >
              <Route index element={<Navigate to="/relatorio-midhaus/visao-geral" replace />} />
              <Route path="visao-geral" element={<RelatorioMidhausVisaoGeral />} />
              <Route path="ligacoes" element={<RelatorioMidhausLigacoes />} />
              <Route path="reunioes" element={<RelatorioMidhausReunioes />} />
              <Route path="deals" element={<RelatorioMidhausDeals />} />
              <Route path="paginas" element={<RelatorioMidhausPaginas />} />
              <Route path="atividades" element={<RelatorioMidhausAtividades />} />
              <Route path="supervisao-pre-vendas" element={<RelatorioMidhausPreVendas />} />
              <Route path="closers" element={<RelatorioMidhausClosers />} />
              <Route path="marketing-vendas" element={<RelatorioMidhausMarketingVendas />} />
              <Route path="resultados" element={<RelatorioMidhausResultados />} />
            </Route>
            <Route
              path="/relatorio-atticus/ligacoes-orfas"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<RequireOrgMembership orgId="08ae88ea-ca08-43fc-88f0-8c67b54783fd"><RelatorioAtticusLigacoesOrfas /></RequireOrgMembership>} />}
            />
            {/* Gate de PESSOA aqui; o de ORG é a tabela `pastas_demo_fontes`, lida pela RPC. */}
            <Route
              path="/pastas-demonstracao"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="materiais.pastas_demonstracao.view"><PastasDemonstracaoPage /></RequirePermission>} />}
            />
            {/* Gate de ORG na RPC (`pasta_escritorio_quadro`: membro e escritório do rateio). */}
            <Route
              path="/pastas"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<PastasEscritorioPage />} />}
            />
            <Route
              path="/relatorio-atticus"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<OrgRestrictedRoute allowedOrgIds={['08ae88ea-ca08-43fc-88f0-8c67b54783fd']}><RelatorioAtticusShell /></OrgRestrictedRoute>} />}
            >
              <Route index element={<Navigate to="/relatorio-atticus/visao-geral" replace />} />
              <Route path="visao-geral" element={<RelatorioAtticusVisaoGeral />} />
              <Route path="ligacoes" element={<RelatorioAtticusLigacoes />} />
              <Route path="reunioes" element={<RelatorioAtticusReunioes />} />
              <Route path="deals" element={<RelatorioAtticusDeals />} />
              <Route path="paginas" element={<RelatorioAtticusPaginas />} />
              <Route path="atividades" element={<RelatorioAtticusAtividades />} />
              <Route path="supervisao-pre-vendas" element={<RelatorioAtticusPreVendas />} />
              <Route path="closers" element={<RelatorioAtticusClosers />} />
              <Route path="marketing-vendas" element={<RelatorioAtticusMarketingVendas />} />
            </Route>
            {/* Gate de ORG = Atticus, no shell. Gate de PESSOA por view: a Carteira (matriz = carteira
                mês a mês) pede `operacoes.carteira.view`; as Teses pedem `operacoes.teses.view`. A raiz
                manda para a primeira view permitida. */}
            <Route
              path="/relatorio-operacoes"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<OrgRestrictedRoute allowedOrgIds={['08ae88ea-ca08-43fc-88f0-8c67b54783fd']}><RelatorioOperacoesShell /></OrgRestrictedRoute>} />}
            >
              <Route index element={<RelatorioOperacoesIndex />} />
              <Route path="carteira" element={<RequirePermission k="operacoes.carteira.view"><RelatorioOperacoesCarteira /></RequirePermission>} />
              <Route path="teses" element={<RequirePermission k="operacoes.teses.view"><RelatorioOperacoesTeses /></RequirePermission>} />
              {/* Página por tese (Previdenciário, Trabalhista…): mesma chave, sem permissão por tese (D2-a). */}
              <Route path="teses/:slug" element={<RequirePermission k="operacoes.teses.view"><RelatorioOperacoesTeses /></RequirePermission>} />
            </Route>
            <Route
              path="/relatorio-squad"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<OrgRestrictedRoute allowedOrgIds={SQUAD_ORG_IDS}><RelatorioSquad /></OrgRestrictedRoute>} />}
            />
            <Route
              path="/gestao-agenda"
              /* Sem OrgRestrictedRoute: o gate é de PESSOA (`desempenho.gestao_agenda.manage`),
                 não de org. O allowlist com o UUID da Midhaus barrava org nova (Atticus, 15/09)
                 mesmo com o grupo de acesso já tendo a permissão. */
              element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="desempenho.gestao_agenda.manage"><GestaoAgendaPage /></RequirePermission>} />}
            />
            {/* Booking Links (Agendamento). Lista/editor: chave `desempenho.gestao_agenda.manage`
                — a MESMA que a sidebar consulta, para menu e rota nao divergirem.
                Disponibilidade: qualquer autenticado edita a própria (RLS user_id=auth.uid()). */}
            <Route
              path="/agendamento"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="desempenho.gestao_agenda.manage"><AgendamentoList /></RequirePermission>} />}
            />
            <Route
              path="/agendamento/disponibilidade"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<AgendamentoDisponibilidade />} />}
            />
            <Route
              path="/agendamento/:id"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="desempenho.gestao_agenda.manage"><AgendamentoEditor /></RequirePermission>} />}
            />
            <Route
              path="/metricas"
              element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="desempenho.metricas.view"><MetricasInsightsLayout /></RequirePermission>} />}
            >
              <Route index element={<MetricasIndexPage />} />
              <Route path=":dashboardId" element={<AnalysisLabDashboard />} />
            </Route>
            <Route path="/deals" element={<Navigate to="/deals/kanban" replace />} />
            <Route path="/deals/new" element={<Navigate to="/deals/kanban?create=true" replace />} />
            <Route path="/deals/import" element={<PrivateRoute authLoading={authLoading} session={session} element={<DealImport />} />} />
            <Route path="/deals/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<DealProfile />} />} />
            <Route path="/deals/:id/diagnostico" element={<PrivateRoute authLoading={authLoading} session={session} element={<DealDiagnosis />} />} />
            <Route path="/deals/:id/edit" element={<PrivateRoute authLoading={authLoading} session={session} element={<DealEdit />} />} />
            <Route path="/deals/kanban" element={<PrivateRoute authLoading={authLoading} session={session} element={<DealsKanban />} />} />
            <Route path="/deals/list" element={<PrivateRoute authLoading={authLoading} session={session} element={<Deals />} />} />
            <Route path="/deals/forecast" element={<PrivateRoute authLoading={authLoading} session={session} element={<DealsForecast />} />} />
            <Route path="/calls/:activityId/transcription" element={<PrivateRoute authLoading={authLoading} session={session} element={<CallTranscription />} />} />
            <Route path="/meetings/:activityId/transcription" element={<PrivateRoute authLoading={authLoading} session={session} element={<MeetingTranscription />} />} />
            <Route path="/agentes" element={<PrivateRoute authLoading={authLoading} session={session} element={<AgentsList />} />} />
            <Route path="/agentes/novo" element={<PrivateRoute authLoading={authLoading} session={session} element={<AgentConfigPage />} />} />
            <Route path="/agentes/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<AgentConfigPage />} />} />
            <Route path="/whatsapp-oficial" element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="comunicacao.whatsapp_oficial.view"><WhatsAppOficial /></RequirePermission>} />} />
            <Route path="/whatsapp-saude" element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="comunicacao.whatsapp_saude.view"><WhatsAppSaude /></RequirePermission>} />} />
            <Route path="/sms" element={<PrivateRoute authLoading={authLoading} session={session} element={<SmsPage />} />} />

            {/* Vox AI */}
            <Route path="/vox" element={<PrivateRoute authLoading={authLoading} session={session} element={<VoxAssistantList />} />} />
            <Route path="/vox/assistants" element={<PrivateRoute authLoading={authLoading} session={session} element={<VoxAssistantList />} />} />
            <Route path="/vox/assistants/new" element={<PrivateRoute authLoading={authLoading} session={session} element={<VoxAssistantCreate />} />} />
            <Route path="/vox/assistants/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<VoxAssistantDetail />} />} />

            <Route path="/vox/campaigns" element={<PrivateRoute authLoading={authLoading} session={session} element={<VoxCampaignList />} />} />
            <Route path="/vox/campaigns/new" element={<PrivateRoute authLoading={authLoading} session={session} element={<VoxCampaignCreate />} />} />
            <Route path="/vox/campaigns/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<VoxCampaignDetail />} />} />
            <Route path="/vox/reports" element={<PrivateRoute authLoading={authLoading} session={session} element={<VoxReports />} />} />

            {/* Discadora Sonax */}
            <Route path="/discadora" element={<PrivateRoute authLoading={authLoading} session={session} element={<SonaxCampaignList />} />} />
            <Route path="/discadora/operar" element={<PrivateRoute authLoading={authLoading} session={session} element={<SonaxDialerOperate />} />} />
            <Route path="/discadora/nova" element={<PrivateRoute authLoading={authLoading} session={session} element={<SonaxCampaignCreate />} />} />
            <Route path="/discadora/:id" element={<PrivateRoute authLoading={authLoading} session={session} element={<SonaxCampaignDetail />} />} />
            <Route path="/discadora/:id/editar" element={<PrivateRoute authLoading={authLoading} session={session} element={<SonaxCampaignEdit />} />} />

            <Route path="/lead-scoring" element={<PrivateRoute authLoading={authLoading} session={session} element={<LeadScoringRedirect />} />} />
            <Route path="/settings" element={<PrivateRoute authLoading={authLoading} session={session} element={<ProtectedRoute><Settings /></ProtectedRoute>} />} />
            <Route path="/configuracoes/integracoes/zapsign" element={<PrivateRoute authLoading={authLoading} session={session} element={<ProtectedRoute><ZapSignSettings /></ProtectedRoute>} />} />
            {/* A pagina ZapSign passou a se chamar Documentos (Onda 1 do modulo de Projetos).
                A tabela `contracts` e 100% documento de assinatura — sem valor, vigencia,
                parcela ou produto. O contrato COMERCIAL chega na Onda 4, vindo do Nexus, e e
                ele que merece o nome. Ver docs/specs/modulo-projetos.md §7.
                `RequirePermission` e nao `ProtectedRoute`: correcao que veio da main. A sidebar
                mostrava a pagina para `manager` (canAccessAnalytics) e a rota negava por
                `useIsAdmin` (so admin), entao o gestor via o item e levava "Acesso negado" no
                clique. Lendo a MESMA chave nos dois lugares a divergencia some. */}
            <Route path="/documentos" element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="clientes.contratos.view"><ContractsDashboard /></RequirePermission>} />} />
            {/* /contratos serve os DOCUMENTOS de assinatura (ZapSign), SEM redirect: e a mesma
                tela de /documentos, alcancavel pelos dois caminhos. O contrato COMERCIAL
                (client_contracts) vive em /operacoes/contratos e nao disputa este endereco —
                sao coisas diferentes com nomes parecidos, e cada uma tem o seu.

                ⚠️ O redirect temporario "REMOVER NA ONDA 4" que existia aqui foi RESOLVIDO, nao
                descartado: a Onda 4 chegou, o contrato comercial nasceu em /operacoes/contratos,
                e a decisao foi manter /contratos nos documentos. O aviso perdeu o objeto.

                ⚠️ E o gate e o MESMO de /documentos, com a MESMA chave. Deixar este caminho em
                `ProtectedRoute` enquanto o outro exige a permissao faria dele a porta dos fundos
                do outro — dois enderecos para a mesma tela precisam do mesmo cadeado. */}
            <Route path="/contratos" element={<PrivateRoute authLoading={authLoading} session={session} element={<RequirePermission k="clientes.contratos.view"><ContractsDashboard /></RequirePermission>} />} />
            {/* Portal: as views sao rotas internas (/operacoes/projetos, /portfolios) */}
            <Route path="/operacoes/*" element={<PrivateRoute authLoading={authLoading} session={session} element={<OperacoesPortal />} />} />
            <Route path="/materiais/*" element={<PrivateRoute authLoading={authLoading} session={session} element={<MateriaisPortal />} />} />
            {/* Objetivos (OKR): gate de org `okr_enabled` no portal; chaves `objetivos.*` por rota. */}
            <Route path="/objetivos/*" element={<PrivateRoute authLoading={authLoading} session={session} element={<ObjetivosPortal />} />} />
            <Route path="/profile" element={<PrivateRoute authLoading={authLoading} session={session} element={<Profile />} />} />
            {/* `/admin` foi aposentada em 08/09/2026. As duas abas dela viviam em `/settings`:
                "Configurações" == Organização › Informações (`OrganizationInfoTab`, os mesmos 6
                campos e o mesmo update em `organizations`) e "Usuários" == `UsersAndTeamsManager`.
                Manter as duas era o que fazia o CRM ter gestores de usuário concorrentes — trocar
                o papel num e o grupo não acompanhar no outro. Redirect e nao remocao: a URL
                circulava. Vai para `/settings` puro: a página escolhe a aba pelo sessionStorage
                (`settings-active-tab`) e ignora query string — um `?tab=` aqui seria promessa que
                ela não cumpre. */}
            <Route path="/admin" element={<Navigate to="/settings" replace />} />
            <Route path="/admin/organizations" element={<PrivateRoute authLoading={authLoading} session={session} element={<SuperAdminProtectedRoute><AdminOrganizations /></SuperAdminProtectedRoute>} />} />
            <Route path="/admin/loss-reasons" element={<PrivateRoute authLoading={authLoading} session={session} element={<LossReasons />} />} />
                <Route path="/invite/:token" element={<LazyPage><InviteAccept /></LazyPage>} />
                <Route path="/style-guide" element={<LazyPage><StyleGuide /></LazyPage>} />
                <Route path="/docs" element={<PrivateRoute authLoading={authLoading} session={session} element={<Docs />} />} />
                {/* Legacy lab redirects */}
                <Route path="/lab/analysis-v0" element={<Navigate to="/metricas" replace />} />
                <Route path="/lab/analysis-v0/:dashboardId" element={<Navigate to="/metricas" replace />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<LazyPage><NotFound /></LazyPage>} />
                </Route>
                </Routes>
                </Suspense>
              </TourProvider>
            ) : (
              <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<LazyPage><Index /></LazyPage>} />
                <Route path="/auth" element={<LazyPage><Auth /></LazyPage>} />
                <Route path="/invite/:token" element={<LazyPage><InviteAccept /></LazyPage>} />
                <Route path="/style-guide" element={<LazyPage><StyleGuide /></LazyPage>} />
                {/* Sem sessão: super-admin exige login. */}
                <Route path="/super-admin/*" element={<Navigate to="/auth" replace />} />
                {/* O popup do OAuth pode abrir antes da sessão carregar: também nesta árvore. */}
                <Route path="/email/conectado" element={<LazyPage><EmailOAuthDone /></LazyPage>} />
                {/* Pedido de acesso e decisão: PÚBLICOS. Sem estas duas linhas o pedido cairia
                    no PortalAuthRedirect abaixo e mandaria para o login exatamente quem não
                    tem login. Casam por ranking, antes do splat. */}
                <Route path="/relatorios/acesso/decisao" element={<LazyPage><PortalDecisaoAcesso /></LazyPage>} />
                <Route path="/relatorios/:buSlug/solicitar-acesso" element={<LazyPage><PortalSolicitarAcesso /></LazyPage>} />
                {/* Portal externo sem sessão: guarda o destino e manda logar. Precisa existir
                    NESTA árvore também — o que falta aqui vira NotFound, não redirect. */}
                <Route path="/relatorios/:buSlug/*" element={<PortalAuthRedirect />} />
                <Route path="/sem-acesso" element={<Navigate to="/auth" replace />} />
                <Route path="*" element={<LazyPage><NotFound /></LazyPage>} />
              </Routes>
              </Suspense>
            )}


          {/* Global Compose Modal */}
          {session && (
            <ComposeEmailModal
              open={showFloatingCompose}
              onOpenChange={(open) => {
                setShowFloatingCompose(open);
                if (!open) setComposeInitialData(undefined);
              }}
              onSend={handleSendEmail}
              onSaveDraft={(data) => console.log('Save draft:', data)}
              initialFormData={composeInitialData}
            />
          )}
          </BrowserRouter>
        </ChatVisibilityProvider>
      </EmailContext.Provider>
    </AppRenderController>
  );
};

const App = () => {
  // Public routes — render without any providers, auth, or layout
  if (window.location.pathname.startsWith('/v1/analise-site/')) {
    return (
      <React.Suspense fallback={null}>
        <SiteAnalysisView />
      </React.Suspense>
    );
  }

  // Páginas legais públicas (acessíveis logado ou deslogado, ex.: revisão do Google OAuth)
  if (window.location.pathname.startsWith('/privacidade')) {
    return (
      <React.Suspense fallback={null}>
        <PrivacyPolicy />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/termos')) {
    return (
      <React.Suspense fallback={null}>
        <TermsOfService />
      </React.Suspense>
    );
  }

  // Local presentation viewer: no CRM queries, only an ephemeral same-browser session.
  if (window.location.pathname === '/proposal-live') {
    return <React.Suspense fallback={null}><ProposalLivePage /></React.Suspense>;
  }

  // Link público da apresentação de proposta (/p/:token): sem login, registra quem acessa.
  if (window.location.pathname.startsWith('/p/')) {
    return <React.Suspense fallback={null}><ProposalPublicPage /></React.Suspense>;
  }

  // Booking Links (Agendamento) — páginas públicas, fora de todos os providers/auth.
  // /book/manage/:token antes de /book/:slug (mais específica primeiro).
  if (window.location.pathname.startsWith('/book/manage')) {
    return (
      <React.Suspense fallback={null}>
        <BookingManagePage />
      </React.Suspense>
    );
  }
  if (window.location.pathname.startsWith('/book')) {
    return (
      <React.Suspense fallback={null}>
        <BookingPublicPage />
      </React.Suspense>
    );
  }

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-md text-center space-y-4">
            <h1 className="text-xl font-semibold text-foreground">Erro na Aplicação</h1>
            <p className="text-muted-foreground">{error?.toString() || 'Ocorreu um erro inesperado.'}</p>
            <button onClick={resetError} className="px-4 py-2 bg-primary text-primary-foreground rounded-md">
              Tentar Novamente
            </button>
          </div>
        </div>
      )}>
      <ErrorBoundary>
        <ImpersonationProvider>
          <OrganizationProvider>
            <PageProvider>
              <ActivityProvider>
                <SonaxWidgetProvider>
                  <ConfirmDialogProvider>
                    <Toaster />
                    <Sonner />
                    <GlobalActivityScheduleModal />
                    <AppContent />
                  </ConfirmDialogProvider>
                </SonaxWidgetProvider>
              </ActivityProvider>
            </PageProvider>
          </OrganizationProvider>
        </ImpersonationProvider>
      </ErrorBoundary>
      </Sentry.ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
